import { computed, effect, inject, Injectable, NgZone, signal } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Guid } from 'guid-typescript';
import { BehaviorSubject, filter, firstValueFrom, Observable, Subject } from 'rxjs';
import { YesNoDialogComponent } from '../dialogs/yes-no-dialog/yes-no-dialog';
import { DrawerConfiguration, DrawerPlacement } from '../interfaces/drawer-configuration';
import { SiaComponentConfigPerspectives } from '../interfaces/sia-component-config-perspectives';
import { SiaMenu } from '../interfaces/sia-menu';
import { SiaRouteData, SiaRouteMode } from '../interfaces/sia-navigation';
import { SiaSessionParam } from '../interfaces/sia-session-param';
import { SiaTraceActivity } from '../interfaces/sia-trace-activity';
import { SiaCacheDatabaseService } from './cache-database.service';
import { SiaComponentConfigService } from './component-config.service';
import { removeLocalStorageItems } from './functions-utils.service';
import { jsonDeepClone } from './jsonDeepClone';
import { SiaLayoutService } from './layout.service';
import { SiaMenuService } from './menu.service';
import { notify } from './notify';
import { SiaSessionService } from './session.service';
import { SiaSignalrService } from './signalr.service';
import { SiaSizeDetectorService } from './size-detector.service';
import { SiaSubtitleService } from './subtitle.service';
import { SiaTokenStoreService } from './token-store.service';
import { SiaUsersService } from './users.service';

@Injectable({
  providedIn: 'root',
})
export class SiaNewRouterService {
  #router = inject(Router);
  #tokenStoreService = inject(SiaTokenStoreService);
  #siaMenuService = inject(SiaMenuService);
  #siaSubtitleService = inject(SiaSubtitleService);
  #siaSizeDetectorService = inject(SiaSizeDetectorService);
  #signalrService = inject(SiaSignalrService);
  #siaLayoutService = inject(SiaLayoutService);
  #matBottomSheet = inject(MatBottomSheet);
  #usersService = inject(SiaUsersService);
  #cacheDatabaseService = inject(SiaCacheDatabaseService);
  #ngZone = inject(NgZone);
  #componentConfigService = inject(SiaComponentConfigService);

  #perspective: SiaComponentConfigPerspectives | null = null;
  #configuration = signal<DrawerConfiguration | null>(null);

  // Effect assegnato a una proprieta' per essere registrato nell'injection
  // context del servizio: persiste salvataggi della drawer perspective quando
  // `#configuration` cambia. Non viene letto da nessun'altra parte.
  // eslint-disable-next-line @typescript-eslint/no-unused-private-class-members
  #configurationEffect = effect(async () => {
    const config = this.#configuration();
    if (!config || !this.#perspective) {
      return;
    }
    this.#perspective.isPrivate = true;
    this.#perspective.isPrivateAndSingle = true;
    this.#perspective.jsonConfig = JSON.stringify(config);
    await this.#componentConfigService.setComponentPerspective(this.#perspective);
  });

  private latestGuid: string = '';

  private routeChanged: BehaviorSubject<any> = new BehaviorSubject('');
  routeChanged$: Observable<any> = this.routeChanged.asObservable();

  private routeModeChanged: BehaviorSubject<SiaRouteMode> = new BehaviorSubject({ guid: '', mode: '' });
  routeModeChanged$: Observable<SiaRouteMode> = this.routeModeChanged.asObservable();

  #routeTrace = new Subject<{
    traceStart: SiaTraceActivity;
    traceEnd: SiaTraceActivity;
    menu: SiaMenu;
    params: SiaSessionParam[];
    mode: string;
  }>();
  /**
   * Emette ogni volta che la transizione su un menu produce uno script JS
   * da eseguire (almeno uno fra `traceStartScript` e `traceEndScript` valorizzato).
   * Consumato da `SiaRouteChangeTraceComponent` nella shell.
   */
  readonly routeTrace$ = this.#routeTrace.asObservable();

  private routes = signal<{
    [key: string]: string[];
  }>({
    base: [],
  });
  private secondaryOutletHistory = signal<{
    [key: string]: string[][];
  }>({
    base: [],
  });
  routingData: {
    [guid: string]: SiaRouteData;
  } = {};
  currentOutletsGuid: {
    [outlet: string]: string;
  } = {};

  currentUrl = computed(() => {
    // Start with the base route
    let route: string = 'shell/';
    route += `(base:${this.routes().base?.join('/')}$basetabs$persistent-drawer$persistent-drawertabs$drawer$drawertabs$popup$popuptabs$radar)`;

    // Prima devo togliere i tabs per problemi di replace
    let keys: string[] = Object.keys(this.routes());
    let tabsOutlets = keys.filter((key) => key.includes('tabs')) ?? [];
    for (let index = 0; index < tabsOutlets.length; index++) {
      const key = tabsOutlets[index];
      if (this.routes()[key]?.length) {
        route = route.replace(`$${key}`, `/(tabs:${this.routes()[key].join('/')})`);
      }
    }
    route = route.replace('$persistent-drawertabs', '');
    route = route.replace('$drawertabs', '');
    route = route.replace('$basetabs', '');
    route = route.replace('$popuptabs', '');

    // Poi posso fare il resto
    let nonTabsOutlets = keys.filter((key) => !key.includes('tabs')) ?? [];
    for (let index = 0; index < nonTabsOutlets.length; index++) {
      const key = nonTabsOutlets[index];
      if (key != 'base' && key != 'drawer' && key != 'persistent-drawer') {
        if (this.routes()[key]?.length) {
          route = route.replace(`$${key}`, `//${key}:${this.routes()[key].join('/')}`);
        }
      }
    }

    let drawerOutlet = this.routes().drawer?.length ? `//drawer:${this.routes().drawer.join('/')}` : '';
    let persistentDrawerOutlet = this.routes()['persistent-drawer']?.length
      ? `//persistent-drawer:${this.routes()['persistent-drawer'].join('/')}`
      : '';
    route = route.replace('$drawer', drawerOutlet);
    route = route.replace('$persistent-drawer', persistentDrawerOutlet);
    route = route.replace('$popup', '');
    route = route.replace('$radar', '');
    return route;
  });

  showBackActions = computed(() => {
    let res: { [key: string]: boolean } = {};
    const outletHistory = this.secondaryOutletHistory();
    for (let outletName in outletHistory) {
      if (
        !outletHistory[outletName] ||
        outletHistory[outletName].length <= 1 ||
        !outletHistory[outletName][outletHistory[outletName].length - 1] ||
        outletHistory[outletName][outletHistory[outletName].length - 1].length == 0
      ) {
        res[outletName] = false;
        continue;
      }
      const routingHistory = jsonDeepClone(outletHistory[outletName]);
      routingHistory.shift();

      const lastInOutletComponentId = this.getRoutingData(outletName)?.componentId;
      let outletHasDifferentComponentInHistory = false;
      for (let index = 0; index < routingHistory.length; index++) {
        if (!routingHistory[index] || routingHistory[index].length == 0) {
          continue;
        }
        const outletHistoryGuid = routingHistory[index][0];
        if (this.routingData[outletHistoryGuid]?.componentId !== lastInOutletComponentId) {
          outletHasDifferentComponentInHistory = true;

          break;
        }
      }
      res[outletName] = outletHasDifferentComponentInHistory;
    }
    return res;
  });

  canNavigateBack = computed(() => {
    const baseRoutes = history.state?.routes?.base;
    const routes = this.routes();
    return !history.state?.previousIsLogin;
  });

  #navigatingTimeout: any;
  #isNavigating: Promise<void> | null = null;
  #siaSessionService = inject(SiaSessionService);
  #dialog = inject(MatDialog);
  #previousUrlIsLogin = signal<boolean>(false);
  #previousUrlNotified = signal<string>('');

  constructor() {
    this.loadDrawerPerspective();
    const hasHistoryRoutingData = history.state?.routingData && Object.keys(history.state.routingData).length > 0;
    if (Object.keys(this.routingData).length == 0 && hasHistoryRoutingData) {
      this.retriveHistoryData().then(() => {
        if (this.routes() && Object.keys(this.routes()).length > 0) {
          this.navigate().then(() => {});
        }
      });
    } else if (Object.keys(this.routingData).length == 0) {
      this.parseUrlAndNavigate();
    }

    this.#usersService.userChanged$.subscribe(() => {
      this.loadDrawerPerspective();
    });

    window.addEventListener('popstate', () => {
      this.#ngZone.run(async () => {
        await this.retriveHistoryData();
        await this.navigate();
      });
    });
    this.#router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.#previousUrlIsLogin.set(event.urlAfterRedirects.includes('auth'));
      });

    this.#signalrService.profileNotifications$.subscribe((notification) => {
      if (notification.notificationType != 'route-ai') {
        return;
      }
      var navigationRequest: { menuId: string; filters: any } = JSON.parse(notification.description ?? '{}');
      if (!navigationRequest || !navigationRequest.menuId) {
        return;
      }
      let menuId = navigationRequest.menuId;
      let sessionParams: SiaSessionParam[] = [];
      if (navigationRequest.filters && Array.isArray(navigationRequest.filters)) {
        navigationRequest.filters.forEach((filter) => {
          let keyVal = '';
          let value = '';
          for (let key in filter) {
            if (key.toLowerCase() == 'jsonpropertyname') {
              keyVal = filter[key];
            } else if (key.toLowerCase() == 'extractedvalue') {
              value = filter[key];
            }
          }
          if (keyVal && (typeof value == 'boolean' || value)) {
            sessionParams.push({
              id: keyVal,
              value: value,
            });
          }
        });
      }
      this.navigateMenuWithParams(menuId, sessionParams);
    });
  }

  async loadDrawerPerspective() {
    if (!this.#usersService.getUserData()) {
      return;
    }
    const perspectives = await this.#componentConfigService.getComponentPerspectives('drawer-position', 'drawer');
    if (!perspectives || perspectives.length == 0) {
      this.#perspective = {
        componentId: 'drawer',
        configType: 'drawer-position',
        jsonConfig: '{}',
        isPrivate: true,
        isPrivateAndSingle: true,
      };
    } else {
      this.#perspective = perspectives[0];
    }
    this.#configuration.set(JSON.parse(this.#perspective.jsonConfig || '{}') as DrawerConfiguration);
    this.setDrawerPosition(this.getDrawerPosition());
  }

  private createTraceActivity(
    activityId: string,
    menuId: string,
    params: SiaSessionParam[],
    mode?: string,
    script?: string,
  ) {
    var res: SiaTraceActivity = {
      activityId: activityId,
      script: script,
      params: JSON.stringify(params),
      componentId: menuId,
      mode: mode,
    };
    return res;
  }

  async logout() {
    // Clear all previous token information and related data
    await this.#cacheDatabaseService.clearDatabase();
    this.#tokenStoreService.clearStore();

    // Clear localStorage except for preserved keys
    removeLocalStorageItems();

    // Clear sessionStorage except for APIURLS
    var urls = window.sessionStorage.getItem('APIURLS');
    const keys = Object.keys(window.sessionStorage);
    this.#siaSessionService.clearStorage();
    keys.forEach((key) => {
      if ('apiurls'.indexOf(key.toLowerCase()) == -1) {
        sessionStorage.removeItem(key);
      }
    });
    if (urls) {
      window.sessionStorage.setItem('APIURLS', urls);
    }

    this.clearRoutes();
    // Clear history state
    history.replaceState(null, '', window.location.href);
    this.#router.navigateByUrl('/');
  }
  async verifyIfPageChanged() {
    let afterCloseValue = true;
    if (
      this.#siaSessionService.getSessionParamValue('pageIsModified') &&
      this.routingData[this.latestGuid]?.mode != 'read'
    ) {
      let dialogRef = this.#dialog.open(YesNoDialogComponent, {
        width: '350px',
        data: {
          title: 'Uscire senza salvare ?',
          description: 'Tutte le modifiche verranno perse',
          negativeMeaning: true,
        },
      });
      afterCloseValue = await firstValueFrom(dialogRef.afterClosed());
    }

    return afterCloseValue;
  }

  async navigateBack() {
    if (!(await this.verifyIfPageChanged())) return;
    if (this.canNavigateBack()) {
      history.back();
    } else {
      const user = this.#usersService.getUserData();
      const isMobile = this.#siaSizeDetectorService.currentSize.isMobile;
      const defaultMenu = isMobile
        ? user?.redirectMobileMenuId || user?.redirectMasterMenuId || 'default-login'
        : user?.redirectMasterMenuId || user?.redirectMobileMenuId || 'default-login';
      await this.navigateMenuWithParams(defaultMenu);
    }
  }

  async navigateBackSecondaryOutletOldButWorking(outlet: string) {
    if (!(await this.verifyIfPageChanged())) return;
    this.routes.update((routes) => {
      for (let key in routes) {
        if (key.includes(outlet)) {
          delete routes[key];
        }
      }
      let currentSecondaryOutletHistory = this.secondaryOutletHistory();
      routes[outlet] = currentSecondaryOutletHistory[outlet].pop()!;
      this.secondaryOutletHistory.set(currentSecondaryOutletHistory);

      return jsonDeepClone(routes);
    });
    await this.navigate();
  }

  async navigateBackSecondaryOutlet(outlet: string) {
    if (!(await this.verifyIfPageChanged())) return;
    if (!this.showBackActions()[outlet]) {
      await this.closeOutlet(outlet);
      return;
    }
    // Cattura il menu attualmente mostrato sull'outlet PRIMA di eseguire la
    // pop dell'history: serve come "previousMenu" per gli script di trace.
    const previousOutletData = this.getRoutingData(outlet) ?? undefined;
    this.updateOutletRoutesForBackNavigation(outlet);
    // Dopo la pop, il nuovo guid attivo per l'outlet e' in currentOutletsGuid.
    const newGuid = this.currentOutletsGuid[outlet];
    const newRoutingData = newGuid ? this.routingData[newGuid] : undefined;
    const newMenu = newRoutingData?.menu as SiaMenu | undefined;
    if (newMenu) {
      const newParams = (newRoutingData as any)?.params ?? [];
      const newMode = newRoutingData?.mode ?? 'read';
      this.handleMenuTransition(newMenu, newParams, newMode, previousOutletData);
    }
    await this.navigate();
  }

  private updateOutletRoutesForBackNavigation(outlet: string) {
    this.routes.update((routes) => {
      for (let key in routes) {
        if (key.includes(outlet)) {
          delete routes[key];
        }
      }
      let currentSecondaryOutletHistory = this.secondaryOutletHistory();
      routes[outlet] = currentSecondaryOutletHistory[outlet].pop()!;
      if (routes[outlet] && routes[outlet].length > 0) {
        this.currentOutletsGuid[outlet] = routes[outlet][0];
      } else {
        delete this.currentOutletsGuid[outlet];
      }
      this.secondaryOutletHistory.set({});
      this.secondaryOutletHistory.set(currentSecondaryOutletHistory);

      return jsonDeepClone(routes);
    });
  }

  navigateToComponentJsonConfig(componentType: string, componentId: string) {
    this.navigateWithBaseParts(['core', 'configuration', componentType, componentId], 'configuration', []);
  }

  navigateToSqlEditor(componentId: string) {
    this.navigateWithBaseParts(['core', 'sql-editor', componentId], 'sql-editor', []);
  }

  /**
   * Retrieve the current drawer placement stored in session parameters.
   * Falls back to `'right'` if no value is available.
   */
  getDrawerPosition(): DrawerPlacement {
    const baseComponentId = this.getRoutingData('base')?.componentId;
    const drawerComponentId = this.getRoutingData('drawer')?.componentId;
    const position = this.#configuration()?.[baseComponentId || '']?.[drawerComponentId || ''];
    if (!position) {
      const keys = Object.keys(this.#configuration()?.[baseComponentId || ''] || {});
      if (!keys || !keys.length) {
        return 'right';
      }
      return this.#configuration()?.[baseComponentId || '']?.[keys[0]]!;
    }

    return position!;
  }

  /**
   * Apply a new drawer placement and persist it to the active route's
   * session parameters.  The layout service is also updated immediately.
   */
  setDrawerPosition(position: DrawerPlacement) {
    const baseComponentId = this.getRoutingData('base')?.componentId;
    const drawerComponentId = this.getRoutingData('drawer')?.componentId;
    this.#siaLayoutService.drawerPlacement.set(position);
    if (
      !drawerComponentId ||
      !baseComponentId ||
      (position == 'right' && !this.#configuration()?.[baseComponentId]) ||
      (position == 'bottom' && Object.keys(this.#configuration()?.[baseComponentId] || {}).length == 1)
    ) {
      return;
    }
    // Skip update if position hasn't changed
    const currentPosition = this.#configuration()?.[baseComponentId]?.[drawerComponentId];
    if (currentPosition === position) {
      return;
    }
    this.#configuration.update((current) => ({
      ...current,
      [baseComponentId]: {
        ...current?.[baseComponentId],
        [drawerComponentId]: position,
      },
    }));
  }

  async navigateWithBaseParts(parts: string[], componentId: string, sessionParams: SiaSessionParam[]) {
    var guid = Guid.create().toString();

    this.routingData[guid] = {
      componentId: componentId,
      guid: guid,
      mode: 'read',
      outlet: 'base',
    };

    this.routes.set({
      base: [...parts, guid],
    });
    this.setSessionParams(guid, sessionParams);
    this.latestGuid = guid;
    await this.navigate();
  }
  setSessionParams(guid: string, sessionParams: SiaSessionParam[]) {
    let key = 'params-' + guid?.toLowerCase();
    let currentParams = sessionStorage.getItem(key);
    if (currentParams) {
      let parsedParams: SiaSessionParam[] = JSON.parse(currentParams);
      parsedParams = parsedParams.filter((param) => !sessionParams.some((sp) => sp.id === param.id));
      sessionParams = parsedParams.concat(sessionParams);
    }
    sessionStorage.setItem(key, JSON.stringify(sessionParams));
    sessionStorage.setItem('latest-guid', guid);
  }

  async navigateMenuWithParams(
    componentIdWithOutlet: string,
    params: SiaSessionParam[] = [],
    mode?: string,
    guid: string = Guid.create().toString(),
    subTitle: string = '',
    additionalTitle: string = '',
    mainOutlet: string = 'base',
    routeParams: string[] = [],
  ) {
    // If a navigation is already in progress, ignore this request
    if (this.#isNavigating) {
      return this.#isNavigating;
    }

    clearTimeout(this.#navigatingTimeout);

    this.#isNavigating = new Promise((resolve, reject) => {
      this.#navigatingTimeout = setTimeout(async () => {
        try {
          if (!componentIdWithOutlet) {
            reject(new Error('Component ID is required for navigation'));
            return;
          }
          mainOutlet = !mainOutlet ? 'base' : mainOutlet;
          let menuParts = componentIdWithOutlet.split('^');
          if (
            menuParts[0] === 'drawer' &&
            (this.#siaSizeDetectorService.currentSize.isMobile || mainOutlet === 'popup' || mainOutlet === 'radar')
          ) {
            menuParts[0] = mainOutlet === 'popup' || mainOutlet === 'radar' ? mainOutlet : 'popup';
          }
          const previousMenuId = this.getRoutingData(mainOutlet)?.componentId;

          const menu: SiaMenu | null = await this.#siaMenuService.getMenu(
            menuParts[menuParts.length === 1 ? 0 : 1],
            previousMenuId,
          );
          if (!menu || !menu.id) {
            notify('Impossibile raggiungere il menu', 2000, 'error');
            reject(new Error('Menu not found'));
            return;
          }
          // --- InitParams dal menu ---------------------------------------
          // Il menu puo esporre un campo `initParams` (string JSON) con
          // un array di `SiaSessionParam[]`. Questi vengono usati come
          // default dei session params e possono definire `pageMode` per
          // impostare la modalita' di navigazione quando il chiamante
          // non ha passato `mode` esplicitamente.
          let menuInitParams: SiaSessionParam[] = [];
          if (menu.initParams) {
            try {
              const parsed = JSON.parse(menu.initParams);
              if (Array.isArray(parsed)) {
                menuInitParams = parsed as SiaSessionParam[];
              }
            } catch {
              menuInitParams = [];
            }
          }
          // Merge: initParams prima, poi i `params` del chiamante
          // sovrascrivono i duplicati per `id` (il chiamante vince).
          const mergedParams: SiaSessionParam[] = [
            ...menuInitParams.filter((ip) => !params.some((p) => p.id === ip.id)),
            ...params,
          ];
          params = mergedParams;
          // Risoluzione del mode: se il chiamante non ha passato `mode`
          // (undefined) usiamo il `pageMode` eventualmente presente tra
          // gli initParams, altrimenti fallback a 'read'. Se il chiamante
          // ha passato un qualsiasi valore esplicito (incluso 'read')
          // quello ha sempre la precedenza.
          let resolvedMode: string;
          if (mode === undefined) {
            const pageModeEntry = menuInitParams.find((ip) => ip.id?.toLowerCase() === 'pagemode');
            resolvedMode = pageModeEntry && typeof pageModeEntry.value === 'string' ? pageModeEntry.value : 'read';
          } else {
            resolvedMode = mode;
          }
          mode = resolvedMode;

          if (menuParts[0] === 'close') {
            this.routes.set({
              base: this.routes()['base'],
              'persistent-drawer': this.routes()['persistent-drawer'],
            });
            mainOutlet = 'base';
            menuParts.shift();
          }
          const componentId = menuParts.length === 2 ? menuParts[1] : menuParts[0];

          if (menuParts[0] === 'persistent-drawer') {
            const currentPersistentDrawerGuid = this.currentOutletsGuid['persistent-drawer'];
            const currentPersistentDrawer = currentPersistentDrawerGuid
              ? this.routingData[currentPersistentDrawerGuid]
              : null;

            if (currentPersistentDrawer?.componentId === componentId) {
              await this.closeOutlet('persistent-drawer');
              return resolve();
            }
          }

          try {
            if (menuParts[0] === 'persistent-drawer') {
              if (componentId) {
                window.localStorage.setItem('persistent-drawer', componentId);
              } else {
                window.localStorage.removeItem('persistent-drawer');
              }
            }
          } catch {}

          this.handleMenuTransition(menu, params, mode);

          this.routingData['new'] = {
            sidebarComponentId:
              menu.sidebar || menu.isFavorite ? componentId : this.getRoutingData('base')?.sidebarComponentId,
            componentId: componentId ?? '',
            guid,
            mode,
            menu,
            outlet: menuParts.length === 1 ? mainOutlet : menuParts[0],
            routeParams,
            params,
          } as any;

          this.latestGuid = guid;

          const isBasePage = menuParts.length === 1 && mainOutlet === 'base';
          if (isBasePage) {
            const outletKey = mainOutlet;
            const prevGuid = this.currentOutletsGuid[outletKey];
            const prev = prevGuid ? this.routingData[prevGuid] : null;

            if (
              prev &&
              prev.componentId === (componentId ?? '') &&
              JSON.stringify(prev.routeParams ?? []) === JSON.stringify(routeParams ?? []) &&
              JSON.stringify((prev as any).params ?? []) === JSON.stringify(params ?? []) &&
              prev.mode === mode
            ) {
              return resolve();
            }
            this.setSessionParams(guid, params);
            this.setTitleAndSubtitle(subTitle, guid, additionalTitle);
            this.routes.update((routes) => {
              routes = {
                'persistent-drawer': routes['persistent-drawer'],
              };
              const persistentDrawerGuid = this.currentOutletsGuid['persistent-drawer'];
              this.currentOutletsGuid = {};
              if (persistentDrawerGuid) {
                this.currentOutletsGuid['persistent-drawer'] = persistentDrawerGuid;
              }
              if (menu.menuType === 'core' || menu.menuType === 'custom') {
                routes[mainOutlet] = [menu.menuType ?? '', menu.id ?? '', ...routeParams, guid];
              } else {
                routes[mainOutlet] = ['core', menu.menuType ?? '', menu.id ?? '', ...routeParams, guid];
              }
              return routes;
            });
            this.secondaryOutletHistory.set({});
          } else {
            if (menuParts[0] === 'tabs') {
              menuParts[0] = mainOutlet + menuParts[0];
              this.routingData['new'].outlet = menuParts[0];
            }
            if (menuParts.length === 1) {
              menuParts.unshift(mainOutlet);
            }
            const outletKey = menuParts.length === 1 ? mainOutlet : menuParts[0];
            const prevGuid = this.currentOutletsGuid[outletKey];
            const prev = prevGuid ? this.routingData[prevGuid] : null;
            if (
              prev &&
              prev.componentId === (componentId ?? '') &&
              JSON.stringify(prev.routeParams ?? []) === JSON.stringify(routeParams ?? []) &&
              JSON.stringify((prev as any).params ?? []) === JSON.stringify(params ?? []) &&
              prev.mode === mode
            ) {
              return resolve();
            }
            this.setSessionParams(guid, params);
            this.setTitleAndSubtitle(subTitle, guid, additionalTitle);
            this.routes.update((routes) => {
              this.secondaryOutletHistory.update((secondary) => {
                if (!secondary[menuParts[0]]) secondary[menuParts[0]] = [];
                secondary[menuParts[0]].push(routes[menuParts[0]]);
                return jsonDeepClone(secondary);
              });

              for (let key in routes) {
                if (key === menuParts[0] || (menu.menuType !== 'tabs' && key === menuParts[0] + 'tabs')) {
                  delete routes[key];
                }
              }

              if (menu.menuType !== 'tabs') {
                this.secondaryOutletHistory.update((secondary) => {
                  const tabsOutlet = menuParts[0] + 'tabs';
                  if (secondary[tabsOutlet]) {
                    delete secondary[tabsOutlet];
                  }
                  return jsonDeepClone(secondary);
                });
              }

              if (menu.menuType === 'core' || menu.menuType === 'custom') {
                routes[menuParts[0]] = [menu.menuType ?? '', menu.id ?? '', ...routeParams, guid];
              } else {
                routes[menuParts[0]] = ['core', menu.menuType ?? '', menu.id ?? '', ...routeParams, guid];
              }
              routes[menuParts[0]].unshift(guid);
              return jsonDeepClone(routes);
            });
          }
          for (let outletGuid in this.currentOutletsGuid) {
            if (outletGuid.includes(menuParts.length === 1 ? mainOutlet : menuParts[0])) {
              delete this.currentOutletsGuid[outletGuid];
            }
          }
          this.currentOutletsGuid[menuParts.length === 1 ? mainOutlet : menuParts[0]] = guid;
          this.routingData[guid] = this.routingData['new'];
          delete this.routingData['new'];
          await this.navigate();
          this.#emitRouteModeChanged({
            guid: this.latestGuid,
            mode: this.routingData[this.latestGuid]?.mode ?? 'read',
          });
          resolve();
        } catch (error) {
          reject(error);
        } finally {
          this.#isNavigating = null;
        }
      }, 0);
    });

    return this.#isNavigating;
  }

  private setTitleAndSubtitle(subTitle: string, guid: string, additionalTitle: string) {
    if (subTitle) {
      this.#siaSubtitleService.setSubtitle(guid, subTitle);
    }
    if (additionalTitle) {
      this.#siaSubtitleService.setSubTitleAdditionalInformation(guid, additionalTitle);
    }
  }

  /**
   * Gestisce la transizione verso un nuovo menu: se almeno uno fra
   * `traceStartScript` e `traceEndScript` e' valorizzato, costruisce le due
   * `SiaTraceActivity` ed emette su `routeTrace$`. Il watcher invisibile
   * `SiaRouteChangeTraceComponent` esegue gli script lato client.
   * Se entrambi gli script sono vuoti l'emit viene saltato.
   */
  private handleMenuTransition(
    menu: SiaMenu,
    params: SiaSessionParam[],
    mode: string,
    previousRoutingDataOverride?: SiaRouteData,
  ) {
    // `traceStartScript` viene dal menu NUOVO (quello in cui stiamo entrando),
    // mentre `traceEndScript` va letto dal menu PRECEDENTE (quello che stiamo
    // lasciando). Il menu precedente e' quello correntemente tracciato come
    // "latest" nel routing state, prima di sovrascriverlo con la nuova entry.
    // I flussi che gestiscono outlet secondari (back / pop) passano
    // esplicitamente il previousRoutingData del proprio outlet.
    const previousRoutingData = previousRoutingDataOverride ?? this.getLatestRoutingData();
    const previousMenu = previousRoutingData?.menu;
    const traceStartScript = menu.traceStartScript;
    const traceEndScript = previousMenu?.traceEndScript;

    const traceStart = this.createTraceActivity('ROUTE', menu.id ?? '', params, mode, traceStartScript);
    const traceEnd = this.createTraceActivity(
      'ROUTE',
      previousRoutingData?.componentId ?? '',
      [],
      previousRoutingData?.mode ?? '',
      traceEndScript,
    );
    this.#routeTrace.next({
      traceStart,
      traceEnd,
      menu,
      params,
      mode,
    });
  }

  notifyPageMode(guid: string, mode?: string) {
    mode = !mode ? 'read' : mode;
    this.routingData[guid] = { ...this.routingData[guid], mode: mode };
    this.#emitRouteModeChanged({ guid: guid, mode: mode! });
    history.replaceState(
      {
        ...history.state,
        routingData: jsonDeepClone(this.routingData),
      },
      '',
      window.location.href,
    );
  }

  async closeOutlet(outletName: string) {
    if (!(await this.verifyIfPageChanged())) return;
    let outletNamesToRemove: string[] = [outletName];
    this.routes.update((routes) => {
      delete routes[outletName];
      for (let key in routes) {
        if (key.startsWith(outletName)) {
          delete routes[key];
          outletNamesToRemove.push(key);
        }
      }
      return jsonDeepClone(routes);
    });

    this.secondaryOutletHistory.update((secondary) => {
      delete secondary[outletName];
      for (let key in secondary) {
        if (key.startsWith(outletName)) {
          delete secondary[key];
          outletNamesToRemove.push(key);
        }
      }
      return jsonDeepClone(secondary);
    });
    for (let outletName of outletNamesToRemove) {
      delete this.currentOutletsGuid[outletName];
    }
    // If persistent drawer outlets were removed, clear stored persistent-drawer selection
    try {
      if (outletNamesToRemove.some((n) => n.startsWith('persistent-drawer'))) {
        window.localStorage.removeItem('persistent-drawer');
      }
    } catch (e) {
      // ignore storage errors
    }
    return await this.navigate();
  }

  navigateTo404() {}

  clearRoutes() {
    this.routes.set({});
    this.routingData = {};
    this.currentOutletsGuid = {};
    this.latestGuid = '';
  }

  getLatestRoutingData(): SiaRouteData | undefined {
    return this.routingData[this.latestGuid];
  }

  getRoutingData(outlet: string) {
    let basePageDataGuid = this.currentOutletsGuid[outlet];
    if (!basePageDataGuid) {
      return null;
    }
    return this.routingData[basePageDataGuid];
  }

  private cleanUpRoutingData() {
    const activeGuids = new Set<string>();

    const extractGuids = (parts: string[] | undefined) => {
      if (!parts) return;
      parts.forEach((part) => {
        if (this.routingData[part]) activeGuids.add(part);
      });
    };

    const r = this.routes();
    Object.values(r).forEach((parts) => extractGuids(parts));

    const history = this.secondaryOutletHistory();
    Object.values(history).forEach((historyStack) => {
      historyStack.forEach((parts) => extractGuids(parts));
    });

    Object.values(this.currentOutletsGuid).forEach((guid) => {
      if (guid && this.routingData[guid]) activeGuids.add(guid);
    });

    if (this.latestGuid && this.routingData[this.latestGuid]) {
      activeGuids.add(this.latestGuid);
    }

    const allGuids = Object.keys(this.routingData);
    allGuids.forEach((guid) => {
      if (!activeGuids.has(guid)) {
        delete this.routingData[guid];
        try {
          sessionStorage.removeItem('params-' + guid?.toLowerCase());
        } catch (e) {}
      }
    });
  }

  async navigate() {
    this.cleanUpRoutingData();
    this.#matBottomSheet.dismiss();
    // this.#matDialog.closeAll();
    if (typeof document !== 'undefined' && document.body) {
      const clickEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      });
      document.body.dispatchEvent(clickEvent);
    }
    let skipLocationChange =
      this.currentUrl().includes('//popup:') ||
      this.currentUrl().includes('//drawer:') ||
      this.currentUrl().includes('/(tabs:') ||
      this.currentUrl().includes('//radar:');
    await this.#router.navigateByUrl(this.currentUrl(), {
      skipLocationChange: skipLocationChange,
      state: {
        routingData: jsonDeepClone(this.routingData),
        currentOutletsGuid: jsonDeepClone(this.currentOutletsGuid),
        routes: jsonDeepClone(this.routes()),
        latestGuid: this.latestGuid,
        previousIsLogin: this.#previousUrlIsLogin(),
      },
    });
    if (this.#previousUrlNotified() !== this.currentUrl()) {
      this.#previousUrlNotified.set(this.currentUrl());
      this.#emitRouteChanged();
    }
  }

  private async parseUrlAndNavigate() {
    const hash = window.location.hash; // e.g. #/shell/(base:core/calendar/calendar/guid)
    if (!hash || !hash.includes('shell/')) {
      return;
    }
    const shellPart = hash.substring(hash.indexOf('shell/') + 'shell/'.length);
    // Parse outlets from the URL pattern: (base:part1/part2/...//outlet2:part1/part2/...)
    const outletRegex = /(?:^|\/{2})(\w[\w-]*):([\w\-\/]+)/g;
    // Remove surrounding parentheses
    const cleaned = shellPart.replace(/^\(/, '').replace(/\)$/, '');
    let match: RegExpExecArray | null;
    const parsedRoutes: { [key: string]: string[] } = {};

    while ((match = outletRegex.exec(cleaned)) !== null) {
      const outletName = match[1];
      const parts = match[2].split('/').filter((p) => p);
      parsedRoutes[outletName] = parts;
    }

    if (!parsedRoutes['base'] || parsedRoutes['base'].length === 0) {
      return;
    }

    // Reconstruct routing data from URL parts
    // URL format: menuType/componentId/guid or menuType/componentType/componentId/guid
    for (const outlet of Object.keys(parsedRoutes)) {
      const parts = parsedRoutes[outlet];
      const guid = parts[parts.length - 1];
      // componentId is the second-to-last non-guid part
      const componentId = parts.length >= 3 ? parts[parts.length - 2] : parts[0];

      this.routingData[guid] = {
        componentId: componentId,
        guid: guid,
        mode: 'read',
        outlet: outlet,
      };
      this.currentOutletsGuid[outlet] = guid;
      this.latestGuid = guid;
    }

    this.routes.set(parsedRoutes);
    sessionStorage.setItem('latest-guid', this.latestGuid);

    // Use navigateMenuWithParams for the base component to get full menu data
    const baseGuid = this.currentOutletsGuid['base'];
    const baseData = this.routingData[baseGuid];
    if (baseData?.componentId) {
      await this.navigateMenuWithParams(baseData.componentId, [], 'read', baseGuid);
    }
  }

  private async retriveHistoryData() {
    // Risolvi il nuovo menu di destinazione PRIMA di sovrascrivere
    // `routingData` / `latestGuid`, in modo che `handleMenuTransition` possa
    // ancora leggere il menu precedente da `getLatestRoutingData()`.
    const nextRoutingData = history.state?.routingData ?? {};
    const nextLatestGuid: string = history.state?.latestGuid ?? '';
    const nextMenu: SiaMenu | undefined = nextRoutingData?.[nextLatestGuid]?.menu;
    const previousLatestGuid = this.latestGuid;
    if (nextMenu && nextLatestGuid && nextLatestGuid !== previousLatestGuid) {
      const nextParams = (nextRoutingData[nextLatestGuid] as any)?.params ?? [];
      const nextMode = nextRoutingData[nextLatestGuid]?.mode ?? 'read';
      this.handleMenuTransition(nextMenu, nextParams, nextMode);
    }

    this.routes.set(history.state?.routes ?? { base: [] });
    this.routingData = history.state?.routingData ?? {};
    this.currentOutletsGuid = history.state?.currentOutletsGuid ?? {};
    this.latestGuid = history.state?.latestGuid ?? '';
    this.#previousUrlIsLogin.set(history.state?.previousIsLogin ?? false);
    sessionStorage.setItem('latest-guid', this.latestGuid);

    // Check if we have valid routing data
    const hasValidRoutes =
      this.routes() && Object.keys(this.routes()).some((key) => this.routes()[key] && this.routes()[key].length > 0);

    if (!hasValidRoutes || !this.latestGuid || !this.routingData[this.latestGuid]) {
      // Try to recover navigation from the current URL before falling back
      const hash = window.location.hash;
      if (hash && hash.includes('shell/')) {
        await this.parseUrlAndNavigate();
        return;
      }
      // No valid routing data, redirect to user's default page or default-login
      if (!(await this.#tokenStoreService.isCurrentTokenExpired())) {
        const user = this.#usersService.getUserData();
        const isMobile = this.#siaSizeDetectorService.currentSize.isMobile;
        const defaultMenu = isMobile
          ? user?.redirectMobileMenuId || user?.redirectMasterMenuId || 'default-login'
          : user?.redirectMasterMenuId || user?.redirectMobileMenuId || 'default-login';

        await this.navigateMenuWithParams(defaultMenu);
        return;
      } else {
        this.clearRoutes();
        this.#router.navigate(['#']);
        return;
      }
    }

    if (this.latestGuid && this.routingData[this.latestGuid]) {
      this.notifyPageMode(this.latestGuid, this.routingData[this.latestGuid]?.mode);
    }
  }

  #emitRouteChanged() {
    if (NgZone.isInAngularZone()) {
      this.routeChanged.next(this.latestGuid);
      return;
    }
    this.#ngZone.run(() => {
      this.routeChanged.next(this.latestGuid);
    });
  }

  #emitRouteModeChanged(mode: SiaRouteMode) {
    if (NgZone.isInAngularZone()) {
      this.routeModeChanged.next(mode);
      return;
    }
    this.#ngZone.run(() => {
      this.routeModeChanged.next(mode);
    });
  }
}

export function retriveCurrentRouteData(): SiaRouteData | undefined {
  let outlet: RouterOutlet | any | null = null;
  try {
    outlet = inject(RouterOutlet);
  } catch (e) {
    let siaNewRouterService = inject(SiaNewRouterService);
    return siaNewRouterService.getLatestRoutingData();
  }
  if (!outlet.isActivated) {
    return undefined;
  }
  var guid = outlet?._activatedRoute?.snapshot?.params?.guid;
  if (!guid) {
    return undefined;
  }
  return retriveRouteData(guid);
}

export function retriveRouteData(guid: any) {
  const routerService = inject(SiaNewRouterService);
  var routeData = routerService?.routingData[guid];
  return jsonDeepClone(routeData);
}
